import { GetStaticPropsContext, PreviewData } from 'next/types';
import { ParsedUrlQuery } from 'querystring';
import { useI18n } from 'next-localization';
import { NextRouter } from 'next/router';

export function getQueryStringParameter(param: string, query: ParsedUrlQuery): string {
  if (query == null || query === undefined || param == null || param === undefined) {
    return '';
  }

  for (const queryParam of Object.keys(query)) {
    if (queryParam.toLowerCase() === param.toLowerCase()) {
      const value = query[queryParam];
      return Array.isArray(value) ? value[0] : !value ? '' : value;
    }
  }

  return '';
}

export function isEmpty(obj: object) {
  for (const prop in obj) {
    if (Object.hasOwn(obj, prop)) {
      return false;
    }
  }

  return true;
}

export function isNullOrEmptyObject(obj: object) {
  if (obj == null || obj === undefined) {
    // obj is null or undefined
    return true;
  }

  if (typeof obj !== 'object') {
    // boolean, number, string, function, etc.
    return false;
  }

  const proto = Object.getPrototypeOf(obj);

  // consider `Object.create(null)`, commonly used as a safe map
  // before `Map` support, an empty object as well as `{}`
  if (proto !== null && proto !== Object.prototype) {
    return false;
  }

  return isEmpty(obj);
}

/**
 * This function takes in a string and replaces any opening and closing HTML tags
 * with an empty string.
 * @param str The input string you are checking for HTML tags.
 * @returns a copy of the input string with any HTML tags removed.
 */
export function removeTags(str: string): string {
  if (str === null || str === '') return str;
  else str = str.toString();

  // Regular expression to identify HTML tags in
  // the input string. Replacing the identified
  // HTML tag with a null string.
  return str.replace(/(<([^>]+)>)/gi, '');
}

export function getSlugsFromPageContext(
  context: GetStaticPropsContext<ParsedUrlQuery, PreviewData>,
  keepSiteInSlug = true
): string[] {
  const pathParts = context.params ? (context.params['path'] as string[]) : [];
  if (!pathParts.length) return [];

  if (context.locale?.length) {
    pathParts.unshift(context.locale);
  }

  if (!keepSiteInSlug) {
    return pathParts.filter((path, index) => {
      // first slug contains the multisite part "_site_upmchealthplan" that needs to be stripped out when keepSiteInSlug = false
      if (index === 0 && path.includes('_site_')) return false;
      return true;
    });
  }

  return pathParts;
}

/**
 * This function takes in a string and removes any line-breaks. Line-breaks in Richtext field was causing in issue in rendering of the component
 * Bug# 614394
 * @param str The input string.
 * @returns a copy of the input string with any line-breaks removed.
 */
export function removeLineBreaks(str: string): string {
  if (str === null || str === '') {
    return str;
  }

  str = str.replace(/['\r\n']/g, '');
  str = str.replace(/['\n']/g, '');
  str = str.replace(/['\r']/g, '');
  str = str.replace(/&#160;/g, '\u00A0');

  return str;
}

export function useDictionary(key: string, defaultValue = ''): string {
  const { t } = useI18n();

  return t(key) || defaultValue || key;
}

export function isInternalLink(url: string): boolean {
  return url?.startsWith('/') && !url?.startsWith('/#');
}

export function isExternalLink(
  url: string,
  whiteListDomains: string,
  link: HTMLAnchorElement,
  excludeType = ''
): boolean {
  const ignoreLinks = (url: string): boolean =>
    (excludeType || '').length > 0 ||
    url.startsWith('tel:') ||
    url.startsWith('mailto:') ||
    url.startsWith('javascript:void') ||
    isHeaderLink(link);

  if (whiteListDomains) {
    const $domains = whiteListDomains.toLowerCase().split(',');
    let $isWhiteListedDomain = ignoreLinks(url);

    if (!$isWhiteListedDomain) {
      $domains.forEach(($domain) => {
        $domain = $domain.toLowerCase().trim();

        if (url.indexOf($domain) >= 0) {
          $isWhiteListedDomain = true;
        }
      });
    }

    return !$isWhiteListedDomain;
  } else if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('//')) {
    return true;
  }

  return false;
}

export function findLinkTag(element: HTMLElement): HTMLElement {
  while (element && element.tagName !== 'A') {
    element = element.parentNode as HTMLElement;
  }

  return element;
}

export function handleExternalLinkClick(
  event: MouseEvent,
  whiteListDomains: string,
  externalLinkCallback: (url: string, target?: string | null) => void,
  internalLinkCallback: (url: string, target?: string | null) => void
) {
  let target = event.target as HTMLElement;

  if (target && target.tagName !== 'A') {
    target = findLinkTag(target);
  }

  if (target) {
    const { tagName } = target;

    if (tagName === 'A') {
      const href = target.getAttribute('data-href') || target.getAttribute('href');
      const linkTarget = target.getAttribute('target');
      const excludeType = target.getAttribute('data-exclude-type') || '';

      if (href && !href.startsWith('#') && !excludeType) {
        if (isExternalLink(href, whiteListDomains, target as HTMLAnchorElement, excludeType)) {
          stopEventPropagation(event);
          externalLinkCallback && externalLinkCallback(href, linkTarget);
        } else {
          internalLinkCallback && internalLinkCallback(href, linkTarget);
        }
      }
    }
  }
}

export function enableScrolling($enable = true, $tag = 'html'): void {
  const $elements = document.getElementsByTagName($tag);
  const $element = $elements ? $elements[0] : undefined;

  if ($element) {
    $enable ? $element.classList.remove('no-scroll') : $element.classList.add('no-scroll');
  }
}

export function stopEventPropagation(event: MouseEvent) {
  event.stopPropagation();
  event.stopImmediatePropagation();
  event.preventDefault();
}

function isHeaderLink(link: HTMLAnchorElement) {
  if (!link) {
    return false;
  }

  const header = document.getElementById('header-container');
  const headerLinks = header?.querySelectorAll('a');
  const headerLinksArray = headerLinks ? Array.from(headerLinks) : [];

  return headerLinksArray.length ? headerLinksArray.indexOf(link) >= 0 : false;
}

export const setNodeProperty = (node: Node, property: string, propertyValue: string) => {
  Object.defineProperty(node, property, {
    value: propertyValue,
    writable: false,
  });
};

export const updateLinksHrefNoLinksWarning = () => {
  const $links = document.querySelectorAll('a');

  const $linksWithNonTextNodes = Array.from($links).filter((link) => {
    return Array.from(link.childNodes).some((child) => child.nodeType !== Node.TEXT_NODE);
  });

  $linksWithNonTextNodes.forEach((link) => {
    const $linkRef = link.getAttribute('href') || '';

    $linkRef &&
      link.childNodes.forEach(($tag) => {
        $tag.nodeType === Node.ELEMENT_NODE && setNodeProperty($tag, 'href', $linkRef);
      });
  });
};

export function updateLinksHref() {
  const links = document.querySelectorAll('a');

  links.forEach((link) => {
    const linkRef = link.getAttribute('href') || '';
    const $isInternalLink = isInternalLink(linkRef);
    const $isHeaderLink = isHeaderLink(link);

    if ($isHeaderLink) {
      link.setAttribute('data-exclude-type', 'header');
    } else if ($isInternalLink) {
      link.setAttribute('data-href', linkRef);
      link.setAttribute('href', 'javascript:');
      link.setAttribute('onclick', 'return false;');
    }
  });
}

export const replaceAll = (str: string, find: string, replace: string): string =>
  str.replace(new RegExp(find, 'g'), replace);

export const getCleanID = (id: string, defaultValue = ''): string => {
  id = id || defaultValue;
  id = replaceAll(id, '-', '').replace('{', '').replace('}', '');

  return isValidGUID(id, true) ? id : '';
};

export const isValidGUID = (strGUID: string, cleaned = false): boolean => {
  if (cleaned) {
    return strGUID ? strGUID.length === 32 && !(strGUID.indexOf(' ') >= 0) : false;
  } else {
    return strGUID ? strGUID.length === 38 && strGUID.startsWith('{') && strGUID.endsWith('}') : false;
  }
};

export const isValidSitecoreItemGUID = (value: string): boolean => {
  // Regular expression pattern to match Sitecore item GUID
  const regex = /^[0-9a-f]{8}(?:-[0-9a-f]{4}){3}-[0-9a-f]{12}$|^[0-9a-f]{32}$/i;
  return regex.test(value);
};

export const parseJSON = (jsonString: string) => {
  try {
    return JSON.parse(jsonString);
  } catch (error) {
    console.log(error);
  }

  return null;
};

export const setSessionVariable = (key: string, value: string) => {
  if (typeof window !== 'undefined') {
    localStorage.setItem(key, value);
  }
};

export const getSessionVariable = (key: string): string => {
  let sessionValue = '';

  if (typeof window !== 'undefined') {
    sessionValue = localStorage.getItem(key) || '';
  }

  return sessionValue;
};

export const saveQueryStrings = (router: NextRouter, queryStringKey: string) => {
  setSessionVariable(queryStringKey, JSON.stringify(router.query));
};

export const getSavedPathWithQueryStrings = (queryStringKey: string): string => {
  let savedPathWithQueryStrings = getSessionVariable(queryStringKey) || '';

  if (savedPathWithQueryStrings) {
    const jsonObject = parseJSON(savedPathWithQueryStrings);
    savedPathWithQueryStrings = '';
    let relativePath = '';

    if (jsonObject) {
      const pathArray = jsonObject['path'];

      Array.isArray(pathArray) &&
        pathArray.map((pathPart: string) => {
          if (pathPart && !pathPart.startsWith('_site_')) {
            relativePath = `${relativePath}/${pathPart}`;
          }
        });

      Object.keys(jsonObject).forEach((key) => {
        if (key !== 'path') {
          const qs = `${key}=${jsonObject[key]}`;
          savedPathWithQueryStrings = `${savedPathWithQueryStrings}${savedPathWithQueryStrings ? '&' : '?'}${qs}`;
        }
      });

      if (savedPathWithQueryStrings) {
        savedPathWithQueryStrings = `${relativePath}${savedPathWithQueryStrings}`;
      }
    }
  }

  return savedPathWithQueryStrings;
};

export function getUrlWithoutExtension(url: string, extensions: string[]): string {
  let $newUrl = url;

  url &&
    extensions &&
    extensions.forEach((extension) => {
      if ($newUrl.endsWith(extension)) {
        $newUrl = $newUrl.replace(extension, '');
      }
    });

  return $newUrl === url ? '' : $newUrl;
}

export const getEffectiveMonthandYear = (date: string): { year: number; month: string } => {
  date = date.replaceAll('-', '').replaceAll(':', ''); //standardize the date format

  const monthAbbreviation = [
    'Jan.',
    'Feb.',
    'Mar.',
    'Apr.',
    'May',
    'Jun.',
    'Jul.',
    'Aug.',
    'Sep.',
    'Oct.',
    'Nov.',
    'Dec.',
  ];
  const effectiveMonthAbbr = monthAbbreviation[parseInt(date.substring(4, 6), 10) - 1];
  const effectiveYear = parseInt(date.substring(0, 4), 10);

  const $returnData = { year: effectiveYear, month: effectiveMonthAbbr };
  console.log($returnData, date);

  return $returnData;
};

/**
 *
 * @param {Date} date Date to convert
 * @returns date and time set to UTC
 */
export function getUTCdate(date: Date) {
  const convertDate = new Date(date);
  return new Date(
    Date.UTC(
      convertDate.getFullYear(),
      convertDate.getMonth(),
      convertDate.getDate(),
      convertDate.getHours(),
      convertDate.getMinutes(),
      convertDate.getSeconds()
    )
  );
}

/**
 *
 * @param {url} url string to get the HostURL - e.g. http://cm/default/page
 * @returns string - host url e.g. http://cm
 */
export const getHostURL = (url: string | undefined): string => {
  let $origin = '';

  if (url) {
    try {
      const $url = new URL(url);
      $origin = $url.origin;
    } catch {}
  }

  return $origin;
};

export const getShortLanguageCode = (languageCode: string, transformToUpper = true): string => {
  if (!languageCode) {
    return '';
  }

  if (languageCode.length > 2) {
    languageCode = languageCode.substring(0, 2);
  }

  languageCode = transformToUpper ? languageCode.toLocaleUpperCase() : languageCode;

  return languageCode;
};

export const defaultSiteLanguage = 'en';

export const getLanguageBasedUrl = (url: string, currentLanguage = ''): string => {
  if (!currentLanguage || currentLanguage === defaultSiteLanguage) {
    return url;
  }

  if (url.startsWith(`/`) && !url.startsWith(`/${currentLanguage}/`)) {
    url = `/${currentLanguage}${url}`;
  }

  return url;
};
